import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateTaxRegionApplicationErrors,
  QueryTaxRegionsResponse,
  TaxRegion,
  TaxRegionCreatedEnvelope,
  TaxRegionDeletedEnvelope,
  TaxRegionQuery,
  TaxRegionUpdatedEnvelope,
  TaxRegionsQueryBuilder,
  UpdateTaxRegion,
  UpdateTaxRegionApplicationErrors,
  createTaxRegion as universalCreateTaxRegion,
  deleteTaxRegion as universalDeleteTaxRegion,
  getTaxRegion as universalGetTaxRegion,
  queryTaxRegions as universalQueryTaxRegions,
  typedQueryTaxRegions as universalTypedQueryTaxRegions,
  updateTaxRegion as universalUpdateTaxRegion,
} from './billing-v1-tax-region-tax-regions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/billing' };

export function createTaxRegion(
  httpClient: HttpClient
): CreateTaxRegionSignature {
  return (taxRegion: NonNullablePaths<TaxRegion, `appId` | `country`, 2>) =>
    universalCreateTaxRegion(
      taxRegion,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTaxRegionSignature {
  /**
   * Creates a tax region.
   *
   * Tax regions require a tax calculator `appId`. Call List Tax Calculators to retrieve a list of available calculators for a site.
   *
   * Wix uses tax regions to calculate tax.
   * <!-- Subdivisions are supported for the following countries: `AU`, `BR`, `CA`, `FR`, `DE`, `IN`, `IT`, `MX`, `NL`, `ES`, `AE`, `GB`, `US` -->
   * @param - Tax region to create.
   * @returns Created tax region.
   */
  (taxRegion: NonNullablePaths<TaxRegion, `appId` | `country`, 2>): Promise<
    NonNullablePaths<
      TaxRegion,
      `country` | `subdivision` | `appId` | `taxIncludedInPrice`,
      2
    > & {
      __applicationErrorsType?: CreateTaxRegionApplicationErrors;
    }
  >;
}

export function getTaxRegion(httpClient: HttpClient): GetTaxRegionSignature {
  return (taxRegionId: string) =>
    universalGetTaxRegion(
      taxRegionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTaxRegionSignature {
  /**
   * Retrieves a tax region.
   * @param - ID of the tax region to retrieve.
   * @returns Retrieved tax region.
   */
  (taxRegionId: string): Promise<
    NonNullablePaths<
      TaxRegion,
      `country` | `subdivision` | `appId` | `taxIncludedInPrice`,
      2
    >
  >;
}

export function updateTaxRegion(
  httpClient: HttpClient
): UpdateTaxRegionSignature {
  return (
    _id: string,
    taxRegion: NonNullablePaths<UpdateTaxRegion, `revision`, 2>
  ) =>
    universalUpdateTaxRegion(
      _id,
      taxRegion,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTaxRegionSignature {
  /**
   * Updates a tax region.
   *
   * Each time the tax region is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the tax region.
   * This ensures you're working with the latest tax region and prevents
   * unintended overwrites.
   * @param - Tax region ID.
   * @param - Tax region info.
   * @returns Updated tax region.
   */
  (
    _id: string,
    taxRegion: NonNullablePaths<UpdateTaxRegion, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      TaxRegion,
      `country` | `subdivision` | `appId` | `taxIncludedInPrice`,
      2
    > & {
      __applicationErrorsType?: UpdateTaxRegionApplicationErrors;
    }
  >;
}

export function deleteTaxRegion(
  httpClient: HttpClient
): DeleteTaxRegionSignature {
  return (taxRegionId: string) =>
    universalDeleteTaxRegion(
      taxRegionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTaxRegionSignature {
  /**
   * Deletes a tax region.
   *
   * When a tax region is deleted, tax is not calculated and zero tax will be returned for addresses in this region.
   * @param - ID of the tax region to delete.
   */
  (taxRegionId: string): Promise<void>;
}

export function queryTaxRegions(
  httpClient: HttpClient
): QueryTaxRegionsSignature {
  return () =>
    universalQueryTaxRegions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTaxRegionsSignature {
  /**
   * Creates a query to retrieve a list of tax regions.
   *
   * The `queryTaxRegions()` function builds a query to retrieve a list of tax regions and returns a `TaxRegionsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `TaxRegionsQueryBuilder` functions onto the query. `TaxRegionsQueryBuilder` functions enable you to sort, filter, and control the results that `queryTaxRegions()` returns.
   *
   * `queryTaxRegions()` runs with the following `TaxRegionsQueryBuilder` default that you can override:
   * + `ascending("_id")`
   *
   * The functions that are chained to `queryTaxRegions()` are applied in the order they are called. For example, if you apply `ascending("country")` and then `ascending("subdivision")`, the results are sorted first by the `"country"`, and then, if there are multiple results with the same `"country"`, the items are sorted by `"subdivision"`.
   *
   * The following `TaxRegionsQueryBuilder` functions are supported for the `queryTaxRegions()` function. For a full description of the tax region object, see the object returned for the `items` property in `TaxRegionsQueryResult`.
   */
  (): TaxRegionsQueryBuilder;
}

export function typedQueryTaxRegions(
  httpClient: HttpClient
): TypedQueryTaxRegionsSignature {
  return (query: TaxRegionQuery) =>
    universalTypedQueryTaxRegions(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryTaxRegionsSignature {
  /** */
  (query: TaxRegionQuery): Promise<
    NonNullablePaths<
      QueryTaxRegionsResponse,
      | `taxRegions`
      | `taxRegions.${number}.country`
      | `taxRegions.${number}.subdivision`
      | `taxRegions.${number}.appId`
      | `taxRegions.${number}.taxIncludedInPrice`,
      4
    >
  >;
}

export const onTaxRegionCreated = EventDefinition(
  'wix.billing.v1.tax_region_created',
  true,
  (event: TaxRegionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TaxRegionCreatedEnvelope>();
export const onTaxRegionDeleted = EventDefinition(
  'wix.billing.v1.tax_region_deleted',
  true,
  (event: TaxRegionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TaxRegionDeletedEnvelope>();
export const onTaxRegionUpdated = EventDefinition(
  'wix.billing.v1.tax_region_updated',
  true,
  (event: TaxRegionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TaxRegionUpdatedEnvelope>();

export {
  AccountDetails,
  ActionEvent,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateTaxRegionRequest,
  BulkCreateTaxRegionResponse,
  BulkCreateTaxRegionResult,
  BulkRestoreTaxRegionsRequest,
  BulkRestoreTaxRegionsResponse,
  BulkRestoreTaxRegionsResult,
  ConflictErrorDetails,
  CreateOrGetTaxRegionWithoutValidationRequest,
  CreateOrGetTaxRegionWithoutValidationResponse,
  CreateTaxRegionRequest,
  CreateTaxRegionResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteTaxRegionRequest,
  DeleteTaxRegionResponse,
  DeleteTaxRegionWithoutDomainEventsRequest,
  DeleteTaxRegionWithoutDomainEventsResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetTaxRegionRequest,
  GetTaxRegionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  QueryTaxRegionsRequest,
  QueryTaxRegionsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  TaxRegion,
  TaxRegionCreatedEnvelope,
  TaxRegionDeletedEnvelope,
  TaxRegionQuerySpec,
  TaxRegionUpdatedEnvelope,
  TaxRegionsQueryBuilder,
  TaxRegionsQueryResult,
  UpdateTaxRegion,
  UpdateTaxRegionRequest,
  UpdateTaxRegionResponse,
  WebhookIdentityType,
} from './billing-v1-tax-region-tax-regions.universal.js';
